// ============================================================
// EXPLORE PIPELINE — View every stage without a candidate
// ============================================================
import React, { useState } from 'react';
import { ArrowLeft, ChevronDown, UserPlus, Eye, Phone, Calendar, Star, ClipboardCheck, FileCheck, Award, CheckCircle2 } from 'lucide-react';
import { PHONE_SCREEN_QUESTIONS, INTERVIEW_QUESTIONS, SCORE_FIELDS, TRIAL_CHECKS } from './constants';

export function ExplorePipeline({ navigate }) {
  const [expanded, setExpanded] = useState(null);

  const stages = [
    { name: 'Applied', icon: UserPlus, desc: 'Candidate submits application via the public form or is added manually.', auto: 'Auto sends "Thank you for your application" email.', content: (
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Application Questions (10)</p>
        <div className="space-y-1">
          {['Why do you want to work with Pure Souls specifically?', 'Tell us about a time you helped someone through a difficult moment.', 'What does compassion mean to you in a workplace?', 'This role involves supporting grieving families and caring for pets after they have passed. How do you feel about that responsibility?', 'Tell us about a time you made a mistake at work. What happened, and what did you do next?', 'Tell us about a time you noticed something small that others missed.', 'What does reliability mean to you?', 'How do you respond when someone gives you constructive feedback?', 'What kind of workplace brings out your best?', 'Why should we trust you with work that requires dignity, care, privacy, and attention to detail?'].map((q, i) => (
            <p key={i} className="text-xs p-2 rounded-lg bg-muted/30"><span className="font-semibold text-primary">{i + 1}.</span> {q}</p>
          ))}
        </div>
      </div>
    )},
    { name: 'Application Review', icon: Eye, desc: 'Review application answers and tag each: Strong, Good, Average, Concern, Reject.', action: 'Advance to Phone Screen or Reject.', email: '"We\'d love to have a quick chat" or "Respectful decline"' },
    { name: 'Phone Screen', icon: Phone, desc: '10 to 15 minute phone call to verify the candidate.', content: (
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Phone Screen Questions (6)</p>
        <div className="space-y-1">
          {PHONE_SCREEN_QUESTIONS.map((q, i) => (
            <p key={i} className="text-xs p-2 rounded-lg bg-muted/30"><span className="font-semibold text-primary">{i + 1}.</span> {q}</p>
          ))}
        </div>
      </div>
    ), action: 'Advance to Interview or Reject.', email: '"Interview Invitation" or "Respectful decline"' },
    { name: 'Interview Booked', icon: Calendar, desc: 'Interview scheduled. Candidate notified with date, time, and location.', content: (
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Interview Questions (15)</p>
        <div className="space-y-1">
          {INTERVIEW_QUESTIONS.filter(iq => iq.q).map((iq, i) => (
            <p key={i} className="text-xs p-2 rounded-lg bg-muted/30"><span className="font-semibold text-primary">{i + 1}.</span> {iq.q}</p>
          ))}
        </div>
      </div>
    ), action: 'Mark Interview Done when complete.' },
    { name: 'Interview Completed', icon: Star, desc: 'Fill the Interview Scorecard with 13 weighted categories (1 to 5 scale).', content: (
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Scorecard Categories (13)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {SCORE_FIELDS.map(f => (
            <p key={f.key} className="text-xs p-2 rounded-lg bg-muted/30"><span className="font-semibold">{f.label}</span> <span className="text-muted-foreground">(weight {f.weight})</span></p>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">90%+ Strong, 75-89% Possible, 60-74% Caution, below 60% Do not proceed.</p>
      </div>
    ), action: 'Advance to Paid Trial or Reject.', email: '"Invitation to a Paid Work Trial"' },
    { name: 'Paid Trial', icon: ClipboardCheck, desc: '3 to 4 hour paid work trial. Observe behaviour, care, communication, emotional maturity.', content: (
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Trial Activities (6)</p>
        <div className="space-y-1">
          {['Answer a simulated client phone call', 'Prepare a pet record label with details you provide', 'Walk through the facility and describe what they notice', 'Explain the aquacremation process to a simulated client', 'Tidy and organise a work area', 'Handle an upset client scenario'].map((a, i) => (
            <p key={i} className="text-xs p-2 rounded-lg bg-muted/30"><span className="font-semibold text-primary">{i + 1}.</span> {a}</p>
          ))}
        </div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-3 mb-2">Observation Checklist (14)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {TRIAL_CHECKS.map(t => (
            <p key={t.key} className="text-xs p-2 rounded-lg bg-muted/30">{t.label}</p>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Includes payment tracking: amount, method, bank details.</p>
      </div>
    ), action: 'Advance to Reference Check or Reject.' },
    { name: 'Reference Check', icon: FileCheck, desc: 'Contact 1 to 2 referees. Structured reference form with 8 question areas.', content: (
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Reference Questions</p>
        <div className="space-y-1">
          {['How reliable were they?', 'How did they respond under stress?', 'How did they respond to correction or feedback?', 'Did they take ownership of mistakes?', 'Were they trusted with sensitive responsibilities?', 'How did they treat clients, customers, or vulnerable people?', 'Could they work well without supervision?', 'Would you hire them again?'].map((q, i) => (
            <p key={i} className="text-xs p-2 rounded-lg bg-muted/30"><span className="font-semibold text-primary">{i + 1}.</span> {q}</p>
          ))}
        </div>
      </div>
    ), action: 'Advance to Final Review or Reject.' },
    { name: 'Final Review', icon: Award, desc: 'Review the consolidated candidate dossier and make the hiring decision.', action: 'Make Offer, Hold for Future, or Reject.', email: '"Offer of Employment" or "Respectful decline"' },
    { name: 'Offer Made', icon: Award, desc: 'Offer email sent. Waiting for candidate response.', action: 'Mark as Hired (accepts) or Withdrawn (declines).', auto: 'When hired: vacancy auto closes, staff profile can be created.' },
    { name: 'Hired', icon: CheckCircle2, desc: 'Candidate accepted the offer. Vacancy marked as Filled. Ready for onboarding.', auto: 'Creates staff profile, connects to P28 onboarding pathway, sets 30/60/90 day review dates.' },
  ];

  return (
    <div>
      <button className="ch-back" onClick={() => navigate('dashboard')}><ArrowLeft size={16} /> Back</button>
      <h1 className="text-xl font-bold mb-2">Pipeline Journey</h1>
      <p className="text-sm text-muted-foreground mb-6">Explore every stage of the recruitment process, what happens, and what tools are available.</p>
      <div className="space-y-2">
        {stages.map((stage, i) => {
          const isOpen = expanded === i;
          return (
            <div key={i} className="ps-card-static overflow-hidden">
              <button onClick={() => setExpanded(isOpen ? null : i)} className="w-full flex items-center gap-4 p-4 text-left hover:bg-muted/20 transition-colors">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(185 40% 93%)', color: 'hsl(185 55% 35%)' }}>
                  <stage.icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground">{i + 1}</span>
                    <span className="text-sm font-bold">{stage.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{stage.desc}</p>
                </div>
                <ChevronDown size={16} className={`text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-border/50 pt-3">
                  {stage.auto && <p className="text-xs mb-2"><span className="bg-[hsl(185_40%_93%)] text-[hsl(185_55%_30%)] px-2 py-0.5 rounded-full font-semibold mr-1">Auto</span> {stage.auto}</p>}
                  {stage.email && <p className="text-xs mb-2"><span className="bg-[hsl(38_80%_92%)] text-[hsl(38_70%_25%)] px-2 py-0.5 rounded-full font-semibold mr-1">Email</span> {stage.email}</p>}
                  {stage.action && <p className="text-xs mb-3"><span className="bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-semibold mr-1">Action</span> {stage.action}</p>}
                  {stage.content && <div className="mt-2">{stage.content}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
